import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { apiGet, apiPost, apiPostStream, apiPut } from '../lib/api-client';
import { EmailTemplateSelect } from '../components/documents/TemplateSelects';
import { apiError } from '../lib/utils';
import { EmailFields } from '../components/documents/EmailFields';
import { EmailPreview } from '../components/documents/EmailPreview';
import { InvoiceFormat2 } from '../components/documents/InvoiceFormat2';
import { InvoicePreview, orderRef } from '../components/documents/InvoicePreview';
import { Alert } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { formatMoney } from '../lib/money';
import { useActivity } from '../components/ui/Activity';
import { useNotice } from '../components/ui/Notice';
import { PageHead, SectionCard } from '../components/ui/chrome';
import { defaultSender, type CompanyMailbox } from '../lib/company-mail';

interface InvoiceRow {
  id: string;
  number?: string;
  internalNumber?: string;
  status?: string;
  revision?: number;
  customerId?: string;
  customerName?: string;
  layout?: string;
  currency?: string;
  issuedOn?: string;
  periodStart?: string;
  periodEnd?: string;
  dueOn?: string;
  poNumber?: string;
  notice?: string;
  title?: string;
  validDays?: number;
  billToName?: string;
  billToAddress?: string;
  billToEmail?: string;
  billToDetails?: string;
  bankLines?: [string, string][];
  paymentTerms?: string;
  delivery?: string;
  acceptance?: string;
  purpose?: string;
  scope?: string;
  vatPence?: number | null;
  contactName?: string;
  contactRole?: string;
  contactEmail?: string;
  contactPhone?: string;
  emailSubject?: string;
  emailBody?: string;
  lines?: { description: string; qty: number; unitPence: number; amountPence: number; poNumber?: string; poLineReference?: string }[];
  replaces?: { id: string; number: string; netPence: number; vatPence: number; currency: string };
  deliveryStatus?: string;
  filingStatus?: string;
  lastError?: string;
  billingWarnings?: string[];
}

export function InvoiceReviewPage() {
  const notice = useNotice();
  const activity = useActivity();
  const { id } = useParams();
  const [row, setRow] = useState<InvoiceRow | null>(null);
  const [from, setFrom] = useState<CompanyMailbox>(defaultSender('invoice'));
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState('');
  const [pendingSend, setPendingSend] = useState<boolean | null>(null);

  useEffect(() => {
    if (!id) return;
    void Promise.all([
      apiGet<InvoiceRow>(`/api/invoices/${id}`),
      apiGet<{ id: string; billingEmails?: string[] }[]>('/api/customers'),
    ]).then(([invoice, customers]) => {
      setRow(invoice);
      const customer = customers.find((item) => item.id === invoice.customerId);
      const emails = [invoice.billToEmail || '', ...(customer?.billingEmails || [])].map((email) => email.trim()).filter((email, index, all) => email && all.indexOf(email) === index);
      setTo(emails);
      setSubject(invoice.emailSubject || `${invoice.internalNumber || invoice.number || 'Invoice'} from Maxiron Ltd`);
      setBody(invoice.emailBody || '');
    }).catch((err) => setError(apiError(err, 'Could not open review')));
  }, [id]);

  function askIssue(send: boolean) {
    if (row?.replaces) {
      setPendingSend(send);
      return;
    }
    void issue(send);
  }

  async function issue(send: boolean, confirmed = false) {
    if (!row) return;
    if (row.status === 'draft') await apiPut(`/api/invoices/${row.id}`, { emailSubject: subject, emailBody: body });
    const payload = {
      send,
      to,
      cc,
      dueOn: row.dueOn,
      emailSubject: subject,
      emailBody: body,
      contactName: row.contactName,
      contactRole: row.contactRole,
      contactEmail: row.contactEmail,
      contactPhone: row.contactPhone,
      from,
    };
    if (!send) {
      const sent = await notice.run('Issuing the invoice', () => apiPost(`/api/invoices/${row.id}/issue`, payload), 'Marked issued. No email was sent.', confirmed ? { confirm: false } : undefined);
      if (sent !== undefined) setRow({ ...row, status: 'issued' });
      return;
    }
    const number = row.internalNumber || row.number || 'Invoice';
    const issued = await activity.track({
      title: 'Sending your invoice',
      subtitle: subject || number,
      dockTitle: `Sending invoice ${number}`,
      to: to.join(', '),
      attachments: 'Invoice',
      kindLabel: 'Invoice',
      confirmed,
      steps: [
        ...(row.replaces ? [{ id: 'credit', label: 'Issuing the credit note', detail: row.replaces.number }] : []),
        { id: 'documents', label: 'Documents prepared', detail: number },
        { id: 'email', label: 'Sending email', detail: 'Waiting for the mail service to confirm.' },
        { id: 'sharepoint', label: 'Storing the invoice file' },
        { id: 'crunch', label: 'Issuing in Crunch' },
        { id: 'issued', label: 'Invoice issued' },
      ],
    }, (apply, signal) => apiPostStream(`/api/invoices/${row.id}/issue`, { ...payload, progress: true }, apply, {}, signal));
    if (issued) setRow({ ...row, status: 'issued' });
  }

  const issued = row?.status === 'issued' || row?.status === 'paid';
  const complete = issued && row?.deliveryStatus === 'sent' && row?.filingStatus === 'filed' && !row?.lastError;
  const lines = (row?.lines || []).map((line) => ({ qty: line.qty, description: line.description, unitPence: line.unitPence, amountPence: line.amountPence, orderRef: orderRef(line.poNumber, line.poLineReference) }));
  const documentDate = row?.issuedOn || row?.periodEnd || row?.periodStart || (row?.status === 'draft' ? new Date().toISOString().slice(0, 10) : '');
  const preview = row?.layout === '1'
    ? <InvoicePreview number={row?.internalNumber || row?.number} customerName={row?.customerName || ''} billingEmails={to} billToName={row?.billToName || ''} billToAddress={row?.billToAddress || ''} billToEmail={row?.billToEmail || ''} billToDetails={row?.billToDetails || ''} bankLines={row?.bankLines} title={row?.title || ''} issuedOn={documentDate} dueOn={row?.dueOn || ''} currency={row?.currency || 'GBP'} poNumber="" contactName={row?.contactName || ''} contactRole={row?.contactRole || ''} contactEmail={row?.contactEmail || ''} contactPhone={row?.contactPhone || ''} validDays={row?.validDays || 0} paymentTerms={row?.paymentTerms || ''} delivery={row?.delivery || ''} acceptance={row?.acceptance || ''} purpose={row?.purpose || ''} scope={row?.scope || ''} notice={row?.notice || ''} lines={lines} vatPence={row?.vatPence ?? null} />
    : <InvoiceFormat2 number={row?.internalNumber || row?.number} customerName={row?.customerName || ''} billingEmails={to} billToName={row?.billToName || ''} billToAddress={row?.billToAddress || ''} billToEmail={row?.billToEmail || ''} billToDetails={row?.billToDetails || ''} bankLines={row?.bankLines} title={row?.title || ''} issuedOn={documentDate} dueOn={row?.dueOn || ''} currency={row?.currency || 'GBP'} poNumber="" contactName={row?.contactName || ''} contactRole={row?.contactRole || ''} contactEmail={row?.contactEmail || ''} contactPhone={row?.contactPhone || ''} validDays={row?.validDays || 0} paymentTerms={row?.paymentTerms || ''} delivery={row?.delivery || ''} acceptance={row?.acceptance || ''} purpose={row?.purpose || ''} scope={row?.scope || ''} notice={row?.notice || ''} lines={lines} vatPence={row?.vatPence ?? null} />;

  return (
    <div>
      {row?.billingWarnings?.length ? <Alert error>Billing profile needs attention: {row.billingWarnings.join('; ')}. Confirm the invoice preview before issuing.</Alert> : null}
      <PageHead
        crumbs={[{ label: 'Invoices', to: '/invoices' }, { label: row?.internalNumber || row?.number || 'Invoice', to: `/invoices/${id}` }, { label: 'Review & issue' }]}
        back={{ label: 'Back to invoice', to: `/invoices/${id}` }}
        title="Review & issue"
        subtitle="Check the invoice and the message before sending."
        actions={
          <>
            <Button variant="outline" disabled={!row || issued} onClick={() => askIssue(false)}>Issue without email</Button>
            <Button disabled={!row || complete} onClick={() => askIssue(true)}>{row?.deliveryStatus === 'sent' ? 'Retry filing' : issued ? 'Send invoice' : 'Issue & send'}</Button>
          </>
        }
      />
      {error ? <Alert error>{error}</Alert> : null}
      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <SectionCard title="Email" hint="The message sent with the invoice.">
            <EmailTemplateSelect kind="invoice" values={{ number: row?.internalNumber || row?.number || '', customer: row?.customerName || '', title: row?.title || '', pos: [...new Set((row?.lines || []).map((line) => line.poNumber || '').filter(Boolean))] }} onApply={(nextSubject, nextBody) => { setSubject(nextSubject); setBody(nextBody); }} />
            <EmailFields from={from} to={to} cc={cc} subject={subject} body={body} onFrom={setFrom} onTo={setTo} onCc={setCc} onSubject={setSubject} onBody={setBody} />
          </SectionCard>
          <EmailPreview from={from} recipients={to.join(', ')} cc={cc.join(', ')} subject={subject} body={body} senderName={row?.contactName} senderTitle={row?.contactRole} senderEmail={row?.contactEmail} senderPhone={row?.contactPhone} />
        </div>
        <div className="min-h-0 overflow-y-auto rounded-lg bg-[var(--bo-preview)] p-3">
          <div className="mb-3"><Button variant="outline" onClick={() => row && window.open(`/api/invoices/${row.id}/pdf`, '_blank')}>Download</Button></div>
          {row ? preview : null}
        </div>
      </div>
      <p className="mt-4 text-sm text-[var(--bo-muted)]">Issuing creates a fixed revision and records the recipient and time.</p>
      {pendingSend != null && row?.replaces ? (
        <Dialog title="Credit the issued invoice" onClose={() => setPendingSend(null)} footer={
          <>
            <Button variant="outline" onClick={() => setPendingSend(null)}>Cancel</Button>
            <Button onClick={() => { const send = pendingSend; setPendingSend(null); void issue(send, true); }}>Proceed</Button>
          </>
        }>
          <p className="text-sm">Issuing {row.internalNumber || row.number} will first issue a full credit note against {row.replaces.number} for {formatMoney(row.replaces.netPence, row.replaces.currency)} net and {formatMoney(row.replaces.vatPence, row.replaces.currency)} VAT. That credit note is recorded here, is not sent to Crunch, and is not emailed. Then this invoice is issued{pendingSend ? ' and emailed' : ' and is not emailed'}.</p>
        </Dialog>
      ) : null}
    </div>
  );
}
